import { Camera } from 'lucide-react';
import { useT } from '@/lib/i18n/useT';

/**
 * PRD §11 Screen 1.75 — Photo & Film Notice.
 *
 * Not a consent (no checkbox). Just a one-line notice that the event area
 * may be photographed or filmed; attendees acknowledge by tapping continue.
 *
 * Renders between the LDA age gate (1.5) and the station picker (2). The
 * SurveyWizard gates it on `kit.customization.photoFilmEnabled` — events
 * without media coverage skip the screen entirely.
 *
 * No MUI dependency: ox-design-system has a richer MUI variant; we keep
 * the consumer flow lean (Tailwind + tokens only) so the bundle stays
 * small for stadium-WiFi kiosks.
 */
export function PhotoFilmNoticeScreen({
  onAcknowledge,
  accent,
  primary,
  brandName,
}: {
  onAcknowledge: () => void;
  accent: string;
  primary: string;
  brandName?: string | null;
}) {
  const t = useT();
  const body = brandName
    ? t('publicSurvey.photoFilm.bodyWithBrand', { brand: brandName })
    : t('publicSurvey.photoFilm.body');
  return (
    <div className="space-y-6 text-center">
      <div
        className="mx-auto grid h-16 w-16 place-items-center rounded-full"
        style={{ background: accent }}
      >
        <Camera className="h-7 w-7" style={{ color: primary }} />
      </div>
      <p className="text-xs uppercase tracking-widest opacity-60">
        {t('publicSurvey.photoFilm.eyebrow')}
      </p>
      <h1 className="text-2xl font-bold leading-tight">
        {t('publicSurvey.photoFilm.title')}
      </h1>
      <p className="mx-auto max-w-sm text-sm opacity-80">{body}</p>
      <p className="mx-auto max-w-sm text-[11px] opacity-60">
        {t('publicSurvey.photoFilm.notACheckbox')}
      </p>
      <button
        type="button"
        onClick={onAcknowledge}
        className="rounded-full px-8 py-3 text-base font-bold"
        style={{ backgroundColor: accent, color: primary }}
      >
        {t('publicSurvey.photoFilm.acknowledge')}
      </button>
    </div>
  );
}
